import type { Incident, IncidentGroup } from '../../services/incidentsApi';

const snapReady = (st: string | null | undefined) => String(st || 'PENDING').toUpperCase() === 'AVAILABLE';
const clipReady = (st: string | null | undefined) => String(st || 'PENDING').toUpperCase() === 'AVAILABLE';

/**
 * Per-incident (raw mode) queue evidence column: labels match actual readiness,
 * not alert rollups.
 */
export function getRawModeEvidenceQueueCopy(inc: Incident): { main: string; sub: string } {
  const atts = Array.isArray(inc.evidence?.attachments) ? inc.evidence?.attachments || [] : [];
  const clipAvailableOnEdge = Boolean((inc as any)?.clip_available_on_edge);
  const clipPromotionState = String((inc as any)?.clip_promotion_state || '').toUpperCase();
  if (atts.length > 0) {
    return {
      main: `${atts.length} evidence item${atts.length === 1 ? '' : 's'}`,
      sub: 'Ready to review',
    };
  }

  const hasSnap = snapReady(inc.snapshot_status) || Boolean(inc.evidence?.latest?.snapshot?.filename);
  const hasClip = clipReady(inc.clip_status) || Boolean((inc.evidence as any)?.latest?.video?.filename);
  if (hasSnap && hasClip) {
    return { main: 'Clip ready', sub: 'Snapshot ready' };
  }
  if (hasSnap) {
    return {
      main: 'Snapshot ready',
      sub: clipAvailableOnEdge
        ? (clipPromotionState === 'PROMOTING' ? 'Clip promoting' : 'Clip on edge')
        : String(inc.clip_status || '').toUpperCase() === 'PENDING'
          ? 'Clip pending'
          : 'Evidence package ready',
    };
  }
  if (hasClip) {
    return {
      main: 'Clip ready',
      sub: String(inc.snapshot_status || '').toUpperCase() === 'PENDING' ? 'Snapshot pending' : 'Evidence package ready',
    };
  }

  const snapshotStatus = String(inc.snapshot_status || '').toUpperCase();
  const clipStatus = String(inc.clip_status || '').toUpperCase();
  if (clipAvailableOnEdge) {
    return {
      main: 'Clip on edge',
      sub: snapshotStatus === 'PENDING' ? 'Snapshot pending' : 'Promote clip when needed',
    };
  }
  if (snapshotStatus === 'PENDING' && clipStatus === 'PENDING') {
    return { main: 'Snapshot pending', sub: 'Clip pending' };
  }
  if (clipStatus === 'PENDING') {
    return { main: 'Clip pending', sub: snapshotStatus === 'PENDING' ? 'Snapshot pending' : 'Evidence still preparing' };
  }
  if (snapshotStatus === 'PENDING') {
    return { main: 'Snapshot pending', sub: 'Evidence still preparing' };
  }

  return { main: 'No evidence yet', sub: 'Snapshot or clip pending' };
}

/**
 * Grouped row: `alert_count` is member-observation volume, not clip/snapshot evidence.
 */
export function getGroupedModeEvidenceQueueCopy(row: IncidentGroup): { main: string; sub: string } {
  const related = Math.max(0, Number(row.alert_count || 0));
  const formatted = related.toLocaleString('en-US');
  const noun = related === 1 ? 'observation' : 'observations';
  return {
    main: `${formatted} ${noun}`,
    sub: 'Grouped in this pattern',
  };
}
